import { ORG_LOGO_CONTENT_TYPES } from '@carcheck/shared';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';
import { precheckLogo } from '../_lib/logoUpload';

/**
 * Logo file picker (AUT-403), after the mockup's dashed drop zone. It only
 * PICKS: type and size are pre-checked (the same limits the confirm
 * enforces) and a refused file shows its reason inline; the parent decides
 * whether to upload now (company card) or after create (create page).
 */
export interface LogoPickerProps {
  onPick: (file: File) => void;
  busy?: boolean;
  disabled?: boolean;
  /** Button label — «Выбрать файл» / «Заменить». */
  actionLabel: string;
  /** Shown instead of the hint once a file is chosen. */
  selectedName?: string | null;
}

export const LogoPicker = ({
  onPick,
  busy = false,
  disabled = false,
  actionLabel,
  selectedName = null,
}: LogoPickerProps): ReactElement => {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const change = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reason = precheckLogo(file);
    setRefused(reason === null ? null : t(`companies.logo.errors.${reason}`));
    if (reason === null) onPick(file);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        border: `1.5px dashed ${tokens.border.strong}`,
        borderRadius: '10px',
        bgcolor: tokens.surface.elevated,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {selectedName
            ? t('companies.logo.selected', { name: selectedName })
            : t('companies.logo.drop')}
        </Typography>
        <Typography variant="caption" color={refused ? 'error' : 'text.secondary'}>
          {refused ?? t('companies.logo.hint')}
        </Typography>
      </Box>
      <input
        ref={input}
        type="file"
        hidden
        accept={ORG_LOGO_CONTENT_TYPES.join(',')}
        onChange={change}
        aria-label={t('companies.logo.inputLabel')}
        data-testid="company-logo-input"
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<UploadIcon />}
        onClick={() => input.current?.click()}
        disabled={disabled || busy}
        loading={busy}
      >
        {actionLabel}
      </Button>
    </Box>
  );
};
